import { useRef, useState } from 'react';
import { isString } from '@/utils/isString';
import { safeParseJson } from '@/utils/safeParseJson';

function readStoredValue<T>(key: string, initialValue: T): T {
  const value = window.localStorage.getItem(key);

  if (!value) {
    return initialValue;
  }

  const looksLikeJson = value.startsWith('{') || value.startsWith('[');

  if (!looksLikeJson) {
    return value as T;
  }

  const parsed = safeParseJson<T>(value);

  if (parsed === null) {
    window.localStorage.removeItem(key);
    return initialValue;
  }

  return parsed;
}

export function useLocalStorage<T>(key: string, initialValue: T): [T, (value: T | ((val: T) => T)) => void] {
  const [state, setState] = useState<T>(() => {
    if (typeof window === 'undefined') {
      return initialValue;
    }

    try {
      return readStoredValue(key, initialValue);
    } catch (error) {
      console.error(error);
      return initialValue;
    }
  });

  const stateRef = useRef(state);
  stateRef.current = state;

  const setValue = (value: T | ((val: T) => T)) => {
    try {
      const valueToStore = value instanceof Function ? value(stateRef.current) : value;
      window.localStorage.setItem(key, isString(valueToStore) ? valueToStore : JSON.stringify(valueToStore));
      stateRef.current = valueToStore;
      setState(valueToStore);
    } catch (error) {
      console.error(error);
    }
  };

  return [state, setValue];
}
